/* eslint-disable camelcase */
import { useRouter } from 'next/router';
import React, { useContext, useState, useEffect } from 'react';
import { Button, Alert, Form } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';

import cx from 'classnames';
import moment from 'moment';
import captureExceptionForClientSide from 'utils/captureExceptionForClientSide';
import { getErrorMessage } from 'utils/formErrorUtils';
import { Validation } from 'utils/validationForm';

import GooglePlacesSearch from 'components/Checkout/components/GooglePlacesSearch/GooglePlacesSearch';
import CountrySelectInput from 'components/CountrySelectInput/CountrySelectInput';
import { AuthContext } from 'contexts/authContext';
import { useRecaptchaContext } from 'contexts/RecaptchaContext';
import { IUserProfile } from 'models/userProfile';

type ISignUpProps = {
  onSignUp?(success: boolean, userProfile?: IUserProfile): void;
  essentialFieldsOnly?: boolean;
};
const SignUpForm: React.FC<ISignUpProps> = ({
  onSignUp,
  essentialFieldsOnly,
}) => {
  const { actions, state } = useContext(AuthContext);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    getValues,
    watch,
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [showManualAddressEntry, setShowManualAddressEntry] = useState(false);
  const router = useRouter();
  const { getRecaptchaToken } = useRecaptchaContext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => actions.initialise(), []);
  useEffect(() => {
    if (state.user && !state.user.isAnonymous) {
      router.push('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createUser = async (userProfile: IUserProfile) => {
    setLoading(true);
    try {
      const recaptchaToken = await getRecaptchaToken();
      if (!recaptchaToken) {
        throw new Error('The recaptcha could not be captured');
      }
      const success = await actions.createUser(userProfile, recaptchaToken);
      // In case the user is logged in as anonymous
      if (state.user?.isAnonymous) {
        await actions.linkEmailAccount(
          userProfile.email as string,
          userProfile.password,
        );
      }
      if (success && onSignUp) {
        onSignUp(success, userProfile);
      }
    } catch (error) {
      captureExceptionForClientSide(error, {
        extra: {
          email: userProfile.email,
        },
      });
    }
    setLoading(false);
  };

  const handleSelectAddress = (values: any): void => {
    if (!Object.keys(values).length) {
      return;
    }
    reset({
      ...getValues(),
      ...values,
    });
    setShowManualAddressEntry(true);
  };

  const currentEmail = watch('email', '');

  return (
    <form onSubmit={handleSubmit(createUser)}>
      <div className="form-group">
        <label htmlFor="email">*Email</label>
        <input
          id="email"
          type="text"
          maxLength={255}
          {...register('email', {
            required: Validation.required,
            pattern: Validation.email,
          })}
          className={cx('form-control', {
            'is-invalid': errors?.email,
          })}
          placeholder="Email"
        />
        {errors?.email && (
          <small className="text-danger">{getErrorMessage(errors.email)}</small>
        )}
      </div>
      <div className="form-group">
        <label htmlFor="confirm_email">*Confirm Email</label>
        <input
          id="confirm_email"
          type="text"
          maxLength={255}
          {...register('confirm_email', {
            validate: value =>
              value === currentEmail || 'The email does not match',
          })}
          className={cx('form-control', {
            'is-invalid': errors?.confirm_email,
          })}
          placeholder="Re-enter Email"
        />
        {errors?.confirm_email && (
          <small className="text-danger">
            {getErrorMessage(errors.confirm_email)}
          </small>
        )}
      </div>
      <div className="form-group">
        <label htmlFor="password">*Password</label>
        <input
          id="password"
          type="password"
          maxLength={60}
          {...register('password', {
            required: Validation.required,
            pattern: Validation.passwordComplexity,
          })}
          className={cx('form-control', {
            'is-invalid': errors?.password,
          })}
          placeholder="Password"
        />
        {errors?.password && (
          <small className="text-danger">
            {getErrorMessage(errors.password)}
          </small>
        )}
      </div>
      <div className="form-row">
        <div className="form-group col-12">
          <label htmlFor="first_name">*First Name</label>
          <input
            id="first_name"
            type="text"
            maxLength={255}
            {...register('first_name', { required: Validation.required })}
            className={cx('form-control', {
              'is-invalid': errors?.first_name,
            })}
            placeholder="First Name"
          />
          {errors?.first_name && (
            <small className="text-danger">
              {getErrorMessage(errors.first_name)}
            </small>
          )}
        </div>
        <div className="form-group  col-12">
          <label htmlFor="last_name">*Last Name</label>
          <input
            id="last_name"
            maxLength={255}
            type="text"
            {...register('last_name', { required: Validation.required })}
            className={cx('form-control', {
              'is-invalid': errors?.last_name,
            })}
            placeholder="Last Name"
          />
          {errors?.last_name && (
            <small className="text-danger">
              {getErrorMessage(errors.last_name)}
            </small>
          )}
        </div>
      </div>
      <div className="form-row">
        <div className="col-12 form-group">
          <label htmlFor="mobile_phone">*Mobile phone</label>
          <input
            id="mobile_phone"
            type="tel"
            {...register('mobile_phone', {
              required: Validation.required,
              pattern: Validation.mobilePhone,
            })}
            className={cx('form-control', {
              'is-invalid': errors?.mobile_phone,
            })}
            placeholder="61000000000"
            maxLength={13}
          />
          {errors?.mobile_phone && (
            <small className="text-danger">
              {getErrorMessage(errors.mobile_phone)}
            </small>
          )}
        </div>
        <div className="col-12 form-group">
          <label htmlFor="dob">*Date of birth</label>
          <input
            id="dob"
            type="date"
            {...register('dob', {
              required: Validation.required,
              validate: {
                dob: (value: string) => {
                  const age = moment().diff(value, 'years');
                  return age >= 1 && age < 121
                    ? true
                    : 'Please type a valid date of birth';
                },
              },
            })}
            className={cx('form-control', {
              'is-invalid': errors?.dob,
            })}
            placeholder="Birth of Date"
          />
          {errors?.dob && (
            <small className="text-danger">{getErrorMessage(errors.dob)}</small>
          )}
        </div>
      </div>

      {!essentialFieldsOnly && (
        <div>
          <div className="d-flex justify-content-end">
            <Button
              onClick={() => {
                setShowManualAddressEntry(!showManualAddressEntry);
              }}
              size="sm"
              variant="link"
            >
              {showManualAddressEntry
                ? 'open address search'
                : "Can't find address or outside Australia?"}
            </Button>
          </div>
          {!showManualAddressEntry && (
            <Form.Group>
              <Form.Label>Search Address</Form.Label>
              <GooglePlacesSearch onSelectionMade={handleSelectAddress} />
            </Form.Group>
          )}
          <div className={cx({ 'd-none': !showManualAddressEntry })}>
            <div className="form-group">
              <div className="form-group">
                <label htmlFor="country_code">Country</label>
                <Controller
                  control={control}
                  render={({ field }) => (
                    <CountrySelectInput
                      {...field}
                      id="country_code"
                      className="form-control"
                    />
                  )}
                  name="country_code"
                  defaultValue="AU"
                />
                {errors?.country_code && (
                  <small className="text-danger">
                    {getErrorMessage(errors.country_code)}
                  </small>
                )}
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="address_line_1">Street address</label>
              <input
                id="address_line_1"
                type="text"
                className="form-control"
                placeholder="Address line 1"
                {...register('address_line_1')}
              />
            </div>
            <div className="form-group">
              <label htmlFor="address_line_2">Address 2</label>
              <input
                id="address_line_2"
                type="text"
                className="form-control"
                placeholder="Address line 2"
                {...register('address_line_2')}
              />
            </div>
            <div className="form-group">
              <label htmlFor="address_line_3">City</label>
              <input
                id="address_line_3"
                type="text"
                className="form-control"
                placeholder="City Name"
                {...register('address_line_3')}
              />
            </div>
            <div className="form-row">
              <div className="col-md-3">
                <div className="form-group">
                  <label htmlFor="state">State</label>
                  <input
                    id="state"
                    type="search"
                    className="form-control"
                    placeholder="State"
                    {...register('state')}
                  />

                  {errors?.state && (
                    <small className="text-danger">
                      {getErrorMessage(errors.state)}
                    </small>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="suburb">Suburb</label>
                  <input
                    id="suburb"
                    type="text"
                    className="form-control"
                    placeholder="Suburb"
                    {...register('suburb')}
                  />
                  {errors?.suburb && (
                    <small className="text-danger">
                      {getErrorMessage(errors.suburb)}
                    </small>
                  )}
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <label htmlFor="post_code">Post Code</label>
                  <input
                    id="post_code"
                    type="text"
                    className="form-control"
                    placeholder="Post Code"
                    {...register('post_code')}
                  />
                  {errors?.post_code && (
                    <div className="alert alert-danger">
                      {getErrorMessage(errors.post_code)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {state.error && <Alert variant="danger">{state.error.message}</Alert>}
      <div className="form-group mt-3">
        <Button type="submit" variant="outline-primary" disabled={loading}>
          Sign up
        </Button>
      </div>
    </form>
  );
};

SignUpForm.defaultProps = {
  onSignUp: undefined,
  essentialFieldsOnly: false,
};

export default SignUpForm;
